import { useEffect, useRef, useState } from 'react';
import Raven from 'raven-js';
import I18n from 'I18n';
import FormattedJSXMessage from 'I18n/components/FormattedJSXMessage';
import FormattedMessage from 'I18n/components/FormattedMessage';
import getLang from 'I18n/utils/getLang';
import isValidI18nKey from 'I18n/utils/isValidI18nKey';
import { userInfoSafe } from 'hub-http/userInfo';
import PortalIdParser from 'PortalIdParser';
import styled from 'foundations-theming/utils';
import { EmptyState } from 'foundations-components/transitional/components/EmptyState';
import { LoadingSpinner } from 'foundations-components/transitional/components/LoadingSpinner';
import { getDatasetAccessLevels } from 'reporting-datasets-permissions-lib';
import { AsyncQueryCancelled, AsyncQueryError, AsyncQueryFailed, AsyncQueryTimeout, DeprecatedDataSourcesError, MaximumColumnsCountExceededError } from 'reporting-snowflake/relational/resolve/resolve-records';
import { Link } from 'foundations-components/transitional/components/Link';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const SplashMessage = styled.div.withConfig({
  displayName: "SnowflakeReportError__SplashMessage",
  componentId: "sc-1488kcg-0"
})(({
  theme: _theme
}) => {
  return {
    padding: _theme.space['200'],
    margin: `${_theme.space['100']} auto`,
    textAlign: 'center'
  };
});
function SplashErrorMessage({
  headerText,
  bodyText,
  'data-test-id': dataTestId
}) {
  return /*#__PURE__*/_jsx(EmptyState, {
    "data-test-id": dataTestId,
    layout: "vertical",
    primaryContent: /*#__PURE__*/_jsxs(SplashMessage, {
      children: [/*#__PURE__*/_jsx("h4", {
        children: headerText
      }), /*#__PURE__*/_jsx("p", {
        children: bodyText
      })]
    })
  });
}
function GenericErrorMessage() {
  return /*#__PURE__*/_jsx(SplashErrorMessage, {
    "data-test-id": "generic-error-message",
    headerText: /*#__PURE__*/_jsx(FormattedMessage, {
      message: "reporting-snowflake.report-errors.generic.header"
    }),
    bodyText: /*#__PURE__*/_jsx(FormattedMessage, {
      message: "reporting-snowflake.report-errors.generic.message"
    })
  });
}
function UnavailablePropertyUsageError({
  error
}) {
  var _responseJSON;
  const propertyLabel = (_responseJSON = error.responseJSON) === null || _responseJSON === void 0 || (_responseJSON = _responseJSON.context) === null || _responseJSON === void 0 || (_responseJSON = _responseJSON.propertyName) === null || _responseJSON === void 0 ? void 0 : _responseJSON[0];
  return /*#__PURE__*/_jsx(SplashErrorMessage, {
    "data-test-id": "unavailable-property-usage-error",
    headerText: /*#__PURE__*/_jsx(FormattedMessage, {
      message: "reporting-snowflake.report-errors.invalid-properties.header"
    }),
    bodyText: propertyLabel ? /*#__PURE__*/_jsx(FormattedMessage, {
      message: "reporting-snowflake.report-errors.invalid-properties.known-property-label-message",
      options: {
        propertyLabel
      }
    }) : /*#__PURE__*/_jsx(FormattedMessage, {
      message: "reporting-snowflake.report-errors.invalid-properties.message"
    })
  });
}
function UnavailableObjectUsageError({
  error
}) {
  var _responseJSON2, _objectTypeId$replace;
  const objectTypeId = (_responseJSON2 = error.responseJSON) === null || _responseJSON2 === void 0 || (_responseJSON2 = _responseJSON2.context) === null || _responseJSON2 === void 0 || (_responseJSON2 = _responseJSON2.objectTypeId) === null || _responseJSON2 === void 0 ? void 0 : _responseJSON2[0];
  const objectKey = (_objectTypeId$replace = objectTypeId === null || objectTypeId === void 0 ? void 0 : objectTypeId.replace('_INBOUNDDB', '')) !== null && _objectTypeId$replace !== void 0 ? _objectTypeId$replace : null;
  const sourceLabelI18nKey = `reporting-snowflake.source-labels.${objectKey}.other`;
  const hasSourceLabel = isValidI18nKey(sourceLabelI18nKey);
  if (hasSourceLabel) {
    return /*#__PURE__*/_jsx(SplashErrorMessage, {
      "data-test-id": "unavailable-known-object-usage-error",
      headerText: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "reporting-snowflake.report-errors.invalid-objects.known-object-label.header",
        options: {
          sourceLabel: I18n.text(sourceLabelI18nKey)
        }
      }),
      bodyText: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "reporting-snowflake.report-errors.invalid-objects.known-object-label.message"
      })
    });
  }
  return /*#__PURE__*/_jsx(SplashErrorMessage, {
    "data-test-id": "unavailable-unknown-object-usage-error",
    headerText: /*#__PURE__*/_jsx(FormattedMessage, {
      message: "reporting-snowflake.report-errors.invalid-objects.header"
    }),
    bodyText: /*#__PURE__*/_jsx(FormattedMessage, {
      message: "reporting-snowflake.report-errors.invalid-objects.message"
    })
  });
}
function ArchivedDatasetErrorMessage({
  error
}) {
  var _responseJSON3;
  const isMountedRef = useRef(true);
  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);
  const [userInfoResponse, setUserInfoResponse] = useState('loading');
  useEffect(() => {
    userInfoSafe().then(userInfo => {
      // don't set state on unmounted components
      if (isMountedRef.current) {
        setUserInfoResponse(userInfo);
      }
    }).catch(e => {
      Raven.captureException(e);
      // don't set state on unmounted components
      if (isMountedRef.current) {
        setUserInfoResponse('error');
      }
    });
  }, []);
  if (userInfoResponse === 'loading') {
    // loading
    return /*#__PURE__*/_jsx(LoadingSpinner, {});
  }
  if (userInfoResponse === 'error') {
    // generic archived message if for some reason userInfo fails
    return /*#__PURE__*/_jsx(SplashErrorMessage, {
      headerText: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "reporting-snowflake.report-errors.archived-dataset.header"
      }),
      bodyText: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "reporting-snowflake.report-errors.archived-dataset.generic-body"
      })
    });
  }
  const datasetId = error === null || error === void 0 || (_responseJSON3 = error.responseJSON) === null || _responseJSON3 === void 0 || (_responseJSON3 = _responseJSON3.context) === null || _responseJSON3 === void 0 || (_responseJSON3 = _responseJSON3.datasetId) === null || _responseJSON3 === void 0 ? void 0 : _responseJSON3[0];
  const {
    customDatasetsAccess,
    writeAccess
  } = getDatasetAccessLevels({
    userInfo: userInfoResponse
  });
  if (!datasetId || !customDatasetsAccess || !writeAccess) {
    return /*#__PURE__*/_jsx(SplashErrorMessage, {
      headerText: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "reporting-snowflake.report-errors.archived-dataset.header"
      }),
      bodyText: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "reporting-snowflake.report-errors.archived-dataset.generic-body"
      })
    });
  }
  return /*#__PURE__*/_jsx(SplashErrorMessage, {
    "data-test-id": "archived-dataset-error",
    headerText: /*#__PURE__*/_jsx(FormattedMessage, {
      message: "reporting-snowflake.report-errors.archived-dataset.header"
    }),
    bodyText: /*#__PURE__*/_jsx(FormattedJSXMessage, {
      message: "reporting-snowflake.report-errors.archived-dataset.archive-link-body_jsx",
      options: {
        href: `/reporting-datasets/${PortalIdParser.get()}/archived/view/${datasetId}`
      },
      elements: {
        Link
      }
    })
  });
}
const SnowflakeReportError = ({
  error
}) => {
  var _responseJSON4;
  if (!error) {
    return /*#__PURE__*/_jsx(GenericErrorMessage, {});
  }
  if (error instanceof AsyncQueryCancelled) {
    return /*#__PURE__*/_jsx(SplashErrorMessage, {
      "data-test-id": "async-query-cancelled-error",
      headerText: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "reporting-snowflake.report-errors.generic.header"
      }),
      bodyText: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "reporting-snowflake.report-errors.async-query.cancelled"
      })
    });
  }
  if (error instanceof AsyncQueryFailed) {
    return /*#__PURE__*/_jsx(SplashErrorMessage, {
      headerText: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "reporting-snowflake.report-errors.generic.header"
      }),
      bodyText: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "reporting-snowflake.report-errors.async-query.failed"
      })
    });
  }
  if (error instanceof AsyncQueryTimeout) {
    return /*#__PURE__*/_jsx(SplashErrorMessage, {
      headerText: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "reporting-snowflake.report-errors.generic.header"
      }),
      bodyText: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "reporting-snowflake.report-errors.async-query.timeout"
      })
    });
  }
  if (error instanceof AsyncQueryError) {
    return /*#__PURE__*/_jsx(GenericErrorMessage, {});
  }
  if (error instanceof MaximumColumnsCountExceededError) {
    return /*#__PURE__*/_jsx(SplashErrorMessage, {
      headerText: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "reporting-snowflake.report-errors.maximum-column-count-exceeded.header"
      }),
      bodyText: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "reporting-snowflake.report-errors.maximum-column-count-exceeded.body"
      })
    });
  }
  if (error instanceof DeprecatedDataSourcesError) {
    return /*#__PURE__*/_jsx(SplashErrorMessage, {
      headerText: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "reporting-snowflake.data-source-deprecated.data-not-available-header"
      }),
      bodyText: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "reporting-snowflake.data-source-deprecated.sources-not-available-description"
      })
    });
  }
  const status = error.status;
  const subCategory = (_responseJSON4 = error.responseJSON) === null || _responseJSON4 === void 0 ? void 0 : _responseJSON4.subCategory;
  if (subCategory === 'SqlReportingServiceError.EVENT_DATE_RANGE_LIMIT_ERROR') {
    return /*#__PURE__*/_jsx(SplashErrorMessage, {
      headerText: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "reporting-snowflake.report-errors.event-date-range-limit.header"
      }),
      bodyText: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "reporting-snowflake.report-errors.event-date-range-limit.message"
      })
    });
  }
  if (subCategory === 'SqlReportingServiceError.MULTI_ACCOUNT_CONFIGURATION') {
    return /*#__PURE__*/_jsx(SplashErrorMessage, {
      "data-test-id": "multi-account-configuration-error",
      headerText: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "reporting-snowflake.report-errors.multi-account-configuration.header"
      }),
      bodyText: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "reporting-snowflake.report-errors.multi-account-configuration.message"
      })
    });
  }
  if (subCategory === 'SqlReportingDatasetsServiceError.DATASET_FIELD_NOT_FOUND') {
    return /*#__PURE__*/_jsx(SplashErrorMessage, {
      headerText: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "reporting-snowflake.report-errors.invalid-dataset-properties.header"
      }),
      bodyText: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "reporting-snowflake.report-errors.invalid-dataset-properties.message"
      })
    });
  }
  if (subCategory === 'SqlReportingServiceError.HIDDEN_OBJECT_TYPE_USAGE_ERROR') {
    return /*#__PURE__*/_jsx(UnavailableObjectUsageError, {
      error: error
    });
  }
  if (subCategory === 'SqlReportingServiceError.UNKNOWN_PROPERTY_USAGE_ERROR' || subCategory === 'SqlReportingServiceError.HIDDEN_PROPERTY_USAGE_ERROR') {
    return /*#__PURE__*/_jsx(UnavailablePropertyUsageError, {
      error: error
    });
  }
  if (subCategory === 'SqlReportingServiceError.OPTED_OUT_PORTAL_ERROR') {
    const langToOptOutInfoUrl = {
      en: 'https://www.hubspot.com/snowflake-sub-processor',
      de: 'https://www.hubspot.de/snowflake-sub-processor',
      es: 'https://www.hubspot.es/subprocesador-snowflake',
      fr: 'https://www.hubspot.fr/snowflake-sub-processor',
      ja: 'https://www.hubspot.jp/snowflake-sub-processor',
      pt: 'https://br.hubspot.com/snowflake-sub-processor'
    };
    return /*#__PURE__*/_jsx(EmptyState, {
      primaryContent: /*#__PURE__*/_jsx(FormattedJSXMessage, {
        message: "reporting-snowflake.opted-out.no-display_jsx",
        options: {
          href: langToOptOutInfoUrl[getLang()] || langToOptOutInfoUrl['en']
        },
        elements: {
          Link
        }
      })
    });
  }
  if (subCategory === 'SqlReportingDatasetsServiceError.DATASET_ARCHIVED') {
    return /*#__PURE__*/_jsx(ArchivedDatasetErrorMessage, {
      error: error
    });
  }
  if (status === 403) {
    return /*#__PURE__*/_jsx(SplashErrorMessage, {
      headerText: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "reporting-snowflake.report-errors.access-denied.header"
      }),
      bodyText: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "reporting-snowflake.report-errors.access-denied.message"
      })
    });
  }
  if (status === 502) {
    return /*#__PURE__*/_jsx(SplashErrorMessage, {
      headerText: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "reporting-snowflake.report-errors.bad-gateway.header"
      }),
      bodyText: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "reporting-snowflake.report-errors.bad-gateway.message"
      })
    });
  }
  return /*#__PURE__*/_jsx(GenericErrorMessage, {});
};
export default SnowflakeReportError;